import { Dimensions } from 'react-native'

import { BottomSheetModalProvider } from '@gorhom/bottom-sheet'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { ToastProvider } from '@zet/common/components/toast'
import NetworkListener from '@zet/utils/NetworkListener'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { createModalStack, ModalProvider } from 'react-native-modalfy'
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context'

import modalConfig from '../modals/modalConfig'
import storageUtil from '../utils/storage'

const stack = createModalStack(modalConfig.config, modalConfig.defaultOptions)

const { height, width } = Dimensions.get('window')

const AppProviders = ({
  children,
  queryClient,
  onLayout,
}: {
  children: React.ReactNode
  queryClient: QueryClient
  onLayout: () => void
}) => {
  const isLandscape = width > height

  const clientStorage = {
    setItem: (key: string, value: never) => {
      storageUtil.mmkvStorage.set(key, value)
    },
    getItem: (key: string) => {
      const value = storageUtil.mmkvStorage.getString(key)
      return value === undefined ? null : value
    },
    removeItem: (key: string) => {
      storageUtil.mmkvStorage.delete(key)
    },
  }

  const containerStyle = {
    ...(isLandscape && {
      maxWidth: '640px',
    }),
  }

  const persister = createSyncStoragePersister({ storage: clientStorage })

  return (
    <GestureHandlerRootView className="flex-1" onLayout={onLayout}>
      <SafeAreaProvider className="bg-grays-20 flex-1 items-center border">
        <SafeAreaView className="w-full flex-1 bg-white" style={containerStyle}>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              dehydrateOptions: {
                shouldDehydrateQuery: (query) => {
                  const queryIsReadyForPersistance =
                    query.state.status === 'success'
                  if (queryIsReadyForPersistance) {
                    const { queryKey } = query
                    const excludeFromPersisting = [
                      'submitSelectedDsa',
                      'getAdvisorDetails',
                      'getDsaList',
                    ].includes(queryKey?.[0] || '')
                    return !excludeFromPersisting
                  }
                  return queryIsReadyForPersistance
                },
              },
            }}
          >
            <NetworkListener />
            <ToastProvider>
              <ModalProvider stack={stack}>
                <BottomSheetModalProvider>{children}</BottomSheetModalProvider>
              </ModalProvider>
            </ToastProvider>
          </PersistQueryClientProvider>
        </SafeAreaView>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  )
}

export default AppProviders
